import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { RootState, store } from '../..';
import { EvalSettingSimple, UpdateEvalSettingSimpleRequest } from '@/models/evalSetting/eval-setting-simple';
import { QuestionType } from '@/models/evalSetting/question-type';
import { evalSettingService } from '@/service/api/eval-setting.service';
import FileSaver from 'file-saver';
import { EvalSettingsForAcademicSearch } from '@/models/evalSetting/eval-settings-for-academic-search';
import { PagingData } from '@/models/core/paging-data';
import { EvalSetting, UpdateExamineeStudentsResult } from '@/models/evalSetting/eval-setting';
import { SchoolClass } from '@/models/school-class/school-class';
import { GEnum } from '@/constants/enum.defination';
import { Student } from '@/models/student/student';
import { ReportStatus } from '@/models/evalSetting/report-status';
import { Empty } from '@/models/core/empty';
import { ReportZipUrl } from '@/models/evalSetting/report-zip-url';
import { AddEvalRequest } from '@/models/evalSetting/add-eval-request';
import { DeleteEvalRequest } from '@/models/evalSetting/delete-eval-request';
import { AllEvalSetting } from '@/models/evalSetting/all-eval-setting';
import { EvalSettingMailToggleResponse } from '@/models/evalSetting/eval-setting-mail';

// 受検モデル用のstore
export interface IEvalSettingState {
  evalSettingSimples: PagingData<EvalSettingSimple>; //受検設定一覧
  questionType: QuestionType[]; // 設問タイプリスト
  competencySettingType: GEnum.CompetencySettingType; //設問数設定
  deadline: Date; // 締切日
  schoolClasses: SchoolClass[]; // 受検クラス
  evalSettingDetail: EvalSetting; // 受検設定詳細
  reportStatus: ReportStatus; // フィードバックレポート ダウンロードステータス
  allEvalSettings: AllEvalSetting[]; // 全受検設定
  selectedItem: GEnum.PersonalReportDisplayType; // レポートタイプの選択アイテム
  reportDisplayType: number; // レポート表示タイプ
  reportEnabled: boolean; // レポート表示可否
  isAllExsmCompleated: boolean; // 全試験が完了しているかどうか
  reportSelectItems: GEnum.PersonalReportDisplayType[]; // レポートタイプのドロップダウン
}
@Module({ dynamic: true, store, name: 'evalSettingModule', namespaced: true })
class EvalSettingModule extends VuexModule implements IEvalSettingState {
  evalSettingSimples: PagingData<EvalSettingSimple> = new PagingData<EvalSettingSimple>();
  questionType: QuestionType[] = [];
  competencySettingType: GEnum.CompetencySettingType = GEnum.CompetencySettingType.Customize;
  deadline: Date = new Date();
  schoolClasses: SchoolClass[] = [];
  evalSettingDetail: EvalSetting = new EvalSetting();
  reportStatus: ReportStatus = new ReportStatus();
  allEvalSettings: AllEvalSetting[] = [];
  selectedItem: GEnum.PersonalReportDisplayType = GEnum.PersonalReportDisplayType.NoShow;
  reportDisplayType: number;
  reportEnabled: boolean;
  isAllExsmCompleated: boolean = false;
  reportSelectItems: GEnum.PersonalReportDisplayType[] = [GEnum.PersonalReportDisplayType.NoShow, GEnum.PersonalReportDisplayType.SelfGrowthSupport, GEnum.PersonalReportDisplayType.DiscoverStrengths];

  @Mutation
  setEvalSettingSimples(value: PagingData<EvalSettingSimple>) {
    this.evalSettingSimples = value;
  }

  @Mutation
  setAllEvalSettings(value: AllEvalSetting[]) {
    this.allEvalSettings = value;
  }

  @Mutation
  setQuestionType(value: QuestionType[]) {
    this.questionType = value;
  }

  @Mutation
  setCompetencySettingType(value: GEnum.CompetencySettingType) {
    this.competencySettingType = value;
  }

  @Mutation
  setDeadline(value: Date) {
    this.deadline = value;
  }

  @Mutation
  setSchoolClasses(value: SchoolClass[]) {
    this.schoolClasses = value;
  }

  @Mutation
  setEvalSettingDetail(value: EvalSetting) {
    this.evalSettingDetail = value;
  }

  @Mutation
  setReportStatus(value: ReportStatus) {
    this.reportStatus = value;
  }

  @Mutation
  setSelectedItem(value: GEnum.PersonalReportDisplayType) {
    this.selectedItem = value;
  }

  /**
   * 受検設定取得
   * @param evalSettingID 評価設定ID
   */
  @Action({ rawError: true })
  async fetchEvalSettingDetail(evalSettingID: number) {
    const detail = await evalSettingService.getEvalSettingDetail(evalSettingID);
    const students: Student[] = await evalSettingService.getEvalSettingStudents(evalSettingID);
    detail.students = students;
    this.setEvalSettingDetail(detail);
  }

  /**
   * 受検削除
   * @param evalSettingID 評価設定ID
   */
  @Action({ rawError: true })
  async deleteEvalSettingDetail(evalSettingID: number) {
    await evalSettingService.deleteEvalSetting(evalSettingID);
  }

  /**
   * 受検一覧取得
   * @param evalSettingsForAcademicSearch 受検設定一覧取得用リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchEvalSettingSimples(evalSettingsForAcademicSearch: EvalSettingsForAcademicSearch) {
    const result: PagingData<EvalSettingSimple> = await evalSettingService.getEvalSettingsForAcademic(
      evalSettingsForAcademicSearch.limit,
      evalSettingsForAcademicSearch.offset,
      evalSettingsForAcademicSearch.hasStatusCompleted
    );
    this.setEvalSettingSimples(result);
  }

  /**
   * 全受検取得（対応する年度も併せて取得）
   */
  @Action({ rawError: true })
  async fetchAllEvalSettings() {
    const result: AllEvalSetting[] = await evalSettingService.getAllEvalSettings();
    this.setAllEvalSettings(result);
  }

  /**
   * 受検進捗ダウンロード
   * @param data 受検設定
   */
  @Action({ rawError: true })
  async downloadProgress(data: EvalSettingSimple) {
    const file = await evalSettingService.downloadProgress(data.evalSettingID);
    FileSaver.saveAs(file, `${data.title}.xlsx`);
  }

  /**
   * レポート生成ステータス取得
   * @param evalSettingID
   */
  @Action({ rawError: true })
  async getReportStatus(evalSettingID: number) {
    const reportStatus: ReportStatus = await evalSettingService.getReportStatus(evalSettingID);
    this.setReportStatus(reportStatus);
  }

  /**
   * レポート一括ダウンロード(通常版)
   * @param evalSettingID
   */
  @Action({ rawError: true })
  async getZipReportDownload(evalSettingID: number): Promise<ReportZipUrl> {
    const result: ReportZipUrl = await evalSettingService.getZipReportUrl(evalSettingID);
    return result;
  }

  /**
   * レポート一括ダウンロード(簡易版)
   * @param evalSettingID
   */
  @Action({ rawError: true })
  async getSimpleZipReportDownload(evalSettingID: number): Promise<ReportZipUrl> {
    const result: ReportZipUrl = await evalSettingService.getZipReportUrl(evalSettingID);
    return result;
  }

  /**
   * フィードバックレポートダウンロード予約リクエスト
   * @param evalSettingID
   */
  @Action({ rawError: true })
  requestZipReportDownload(evalSettingID: number): Promise<Empty> {
    return evalSettingService.requestZipReportDownload(evalSettingID);
  }

  /**
   * フィードバックレポート強制生成
   * @param evalSettingID
   */
  @Action({ rawError: true })
  forceGenerateReport(evalSettingID: number): Promise<Empty> {
    return evalSettingService.forceGenerateReport(evalSettingID);
  }

  /**
   * 受検者割り当て情報更新
   * @param evalSettingID
   */
  @Action({ rawError: true })
  updateExamineeStudents(evalSettingID: number): Promise<UpdateExamineeStudentsResult> {
    return evalSettingService.updateExamineeStudents(evalSettingID);
  }

  /**
   * 評価リクエスト追加
   * @param addEvalRequest コースID&生徒ID
   */
  @Action({ rawError: true })
  addEvalRequest(addEvalRequest: AddEvalRequest): Promise<Empty> {
    return evalSettingService.addEvalRequest(addEvalRequest);
  }

  /**
   * 評価リクエストの削除
   * @param deleteEvalRequest コースID&生徒ID
   */
  @Action({ rawError: true })
  deleteEvalRequest(deleteEvalRequest: DeleteEvalRequest): Promise<Empty> {
    return evalSettingService.deleteEvalRequest(deleteEvalRequest);
  }

  /**
   * レポートリンク情報更新
   * @param data EvalSettingSimple
   */
  @Action({ rawError: true })
  updateReportLink(updateEvalSettingSimpleRequest: UpdateEvalSettingSimpleRequest): Promise<Empty> {
    switch (this.selectedItem) {
      case GEnum.PersonalReportDisplayType.SelfGrowthSupport:
        this.reportDisplayType = GEnum.PersonalReportDisplayType.SelfGrowthSupport;
        this.reportEnabled = true;
        break;

      case GEnum.PersonalReportDisplayType.DiscoverStrengths:
        this.reportDisplayType = GEnum.PersonalReportDisplayType.DiscoverStrengths;
        this.reportEnabled = true;
        break;

      case GEnum.PersonalReportDisplayType.NoShow:
      default:
        // 初期値として自己成長支援版を設定する
        this.reportDisplayType = GEnum.PersonalReportDisplayType.SelfGrowthSupport;
        this.reportEnabled = false;
        break;
    }

    return evalSettingService.updateReportLink(updateEvalSettingSimpleRequest, this.reportDisplayType, this.reportEnabled);
  }

  /**
   * メール設定の更新（ON <> OFF）
   * @param evalSettingID
   */
  @Action({ rawError: true })
  toggleMailSetting(evalSettingID: number): Promise<EvalSettingMailToggleResponse> {
    return evalSettingService.toggleMailSetting(evalSettingID);
  }
}

export const evalSettingModule = getModule(EvalSettingModule);
